import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { tr } from '../til';


const inputStyle = {
  padding: '10px 12px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '14px'
};

const card = {
  background: '#fff',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 1px 8px rgba(0,0,0,0.06)'
};

const formatDate = (raw) => {
  const d = new Date(raw);
  return isNaN(d) ? '' : d.toLocaleDateString('uz-UZ');
};

/// Yozuvlarni berilgan kalit bo'yicha guruhlaydi.
const groupBy = (rows, keyFn, labelFn) => {
  const map = new Map();
  for (const row of rows) {
    const key = keyFn(row);
    if (!map.has(key)) map.set(key, { ...labelFn(row), items: [] });
    map.get(key).items.push(row);
  }
  return [...map.values()];
};

function Downloads() {
  const [rows, setRows] = useState([]);
  const [books, setBooks] = useState([]);
  const [mode, setMode] = useState('books'); // 'books' | 'users'
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    const [downloadsRes, booksRes] = await Promise.all([
      axios.get(`${API}/books/downloads`, { headers: { authorization: token } }),
      axios.get(`${API}/books`, { headers: { authorization: token } })
    ]);
    setRows(downloadsRes.data);
    setBooks(booksRes.data);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const query = search.trim().toLowerCase();

  // Kitoblar bo'yicha: hech kim olmagan kitoblar ham ko'rinishi kerak.
  const byBook = books
    .map(b => ({
      key: `book-${b.id}`,
      title: b.title,
      subtitle: b.author || '',
      items: rows.filter(r => r.book_id === b.id)
    }))
    .filter(g =>
      g.title.toLowerCase().includes(query) ||
      g.subtitle.toLowerCase().includes(query)
    );

  const byUser = groupBy(
    rows,
    r => r.user_id,
    r => ({
      key: `user-${r.user_id}`,
      title: r.full_name,
      subtitle: [r.badge_number, r.rank, r.department].filter(Boolean).join(' · ')
    })
  ).filter(g =>
    g.title.toLowerCase().includes(query) ||
    g.subtitle.toLowerCase().includes(query)
  );

  const groups = mode === 'books' ? byBook : byUser;

  const totalDownloads = rows.reduce((sum, r) => sum + r.download_count, 0);
  const booksTaken = new Set(rows.map(r => r.book_id)).size;
  const usersActive = new Set(rows.map(r => r.user_id)).size;

  const tabStyle = (active) => ({
    padding: '8px 18px',
    border: '1px solid #ddd',
    background: active ? '#1a1a2e' : '#fff',
    color: active ? '#fff' : '#666',
    fontSize: '14px',
    cursor: 'pointer'
  });

  return (
    <div>
      <h2 style={{ marginBottom: '20px', fontSize: '22px' }}>{tr("Kitob yuklab olishlari")}</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: tr("Jami kitoblar"), value: books.length, color: '#4f8ef7' },
          { label: tr("Yuklab olingan kitoblar"), value: `${booksTaken} / ${books.length}`, color: '#2d8a2d' },
          { label: tr("Yuklab olgan xodimlar"), value: usersActive, color: '#f7924f' },
          { label: tr("Jami yuklab olishlar"), value: totalDownloads, color: '#7c4ff7' }
        ].map(c => (
          <div key={c.label} style={{ ...card, padding: '18px' }}>
            <div style={{ color: '#888', fontSize: '13px', marginBottom: '6px' }}>{c.label}</div>
            <div style={{ color: c.color, fontSize: '24px', fontWeight: '600' }}>{c.value}</div>
          </div>
        ))}
      </div>

      <div style={card}>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden' }}>
            <button onClick={() => { setMode('books'); setOpen(null); }} style={tabStyle(mode === 'books')}>
              📚 {tr("Kitoblar bo'yicha")}
            </button>
            <button onClick={() => { setMode('users'); setOpen(null); }} style={tabStyle(mode === 'users')}>
              👥 {tr("Xodimlar bo'yicha")}
            </button>
          </div>
          <input
            type="text"
            placeholder={mode === 'books' ? tr("🔍 Kitob nomi yoki muallifi...") : tr("🔍 Xodim ismi yoki logini...")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: '220px' }}
          />
        </div>

        {loading && <p style={{ color: '#999', fontSize: '14px' }}>{tr("Yuklanmoqda...")}</p>}

        {!loading && groups.length === 0 && (
          <p style={{ color: '#999', fontSize: '14px', textAlign: 'center', margin: '20px 0' }}>
            {mode === 'users' && rows.length === 0
              ? tr("Hali hech kim kitob yuklab olmagan.")
              : tr("Hech narsa topilmadi.")}
          </p>
        )}

        {groups.map(group => {
          const isOpen = open === group.key;
          const count = group.items.length;

          return (
            <div key={group.key} style={{ borderBottom: '1px solid #f4f4f4' }}>
              <div
                onClick={() => setOpen(isOpen ? null : group.key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 4px',
                  cursor: count > 0 ? 'pointer' : 'default'
                }}
              >
                <span style={{ color: '#bbb', fontSize: '12px', width: '14px' }}>
                  {count > 0 ? (isOpen ? '▾' : '▸') : ''}
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '500', fontSize: '14px' }}>{group.title}</div>
                  {group.subtitle && (
                    <div style={{ color: '#999', fontSize: '12px', marginTop: '2px' }}>
                      {group.subtitle}
                    </div>
                  )}
                </div>

                <span style={{
                  background: count > 0 ? '#e9f9ed' : '#f5f5f5',
                  color: count > 0 ? '#2d8a2d' : '#aaa',
                  padding: '3px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap'
                }}>
                  {mode === 'books'
                    ? (count > 0 ? tr("{count} xodim oldi", { count }) : tr("hech kim olmagan"))
                    : tr("{count} ta kitob", { count })}
                </span>
              </div>

              {isOpen && count > 0 && (
                <div style={{ padding: '0 0 14px 30px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ color: '#999' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: '500' }}>
                          {mode === 'books' ? tr("Xodim") : tr("Kitob")}
                        </th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: '500' }}>
                          {mode === 'books' ? tr("Login") : tr("Muallifi")}
                        </th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: '500' }}>{tr("Birinchi marta")}</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: '500' }}>{tr("Oxirgi marta")}</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: '500' }}>{tr("Marta")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map(item => (
                        <tr key={item.id} style={{ borderTop: '1px solid #fafafa' }}>
                          <td style={{ padding: '6px 8px' }}>
                            {mode === 'books' ? item.full_name : item.book_title}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#888' }}>
                            {mode === 'books' ? item.badge_number : (item.book_author || '—')}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#888' }}>
                            {formatDate(item.first_downloaded_at)}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#888' }}>
                            {formatDate(item.last_downloaded_at)}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: '#4f8ef7', fontWeight: '600' }}>
                            {item.download_count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Downloads;
